import { useEffect, useCallback } from "react";
import { FilterState } from "../types/types";

/**
 * プロジェクトごとの簡易フィルター設定をLocalStorageに保存・読み込みするフック
 * 対象：myTasksOnly, myRequestsOnly, dueTodayOnly, overdueOnly, starredOnly, showCompleted, showHidden
 */

interface SimpleFilterState {
  myTasksOnly: boolean;
  myRequestsOnly: boolean;
  dueTodayOnly: boolean;
  overdueOnly: boolean;
  starredOnly: boolean;
  showCompleted: boolean;
  showHidden: boolean;
}

const STORAGE_KEY = "taskapp_simple_filters";

/**
 * LocalStorageから特定のプロジェクトのフィルター設定を読み込む
 */
export function loadSimpleFilters(roomId: number): Partial<FilterState> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};

    const allFilters = JSON.parse(stored) as Record<string, SimpleFilterState>;
    const key = String(roomId);

    if (!allFilters[key]) return {};

    return allFilters[key];
  } catch (error) {
    console.error("Failed to load filters from localStorage:", error);
    return {};
  }
}

/**
 * LocalStorageに特定のプロジェクトのフィルター設定を保存する
 */
export function saveSimpleFilters(roomId: number, filters: FilterState): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const allFilters = stored ? JSON.parse(stored) : {};

    const key = String(roomId);
    allFilters[key] = {
      myTasksOnly: filters.myTasksOnly,
      myRequestsOnly: filters.myRequestsOnly,
      dueTodayOnly: filters.dueTodayOnly,
      overdueOnly: filters.overdueOnly,
      starredOnly: filters.starredOnly,
      showCompleted: filters.showCompleted,
      showHidden: filters.showHidden,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(allFilters));
  } catch (error) {
    console.error("Failed to save filters to localStorage:", error);
  }
}

/**
 * フィルター変更時に自動的にLocalStorageへ保存するカスタムフック
 */
export function useFilterStorage(
  roomId: number,
  filters: FilterState,
  skipSave: boolean = false
) {
  // フィルター変更時に自動保存（skipSaveがtrueの場合はスキップ）
  useEffect(() => {
    if (roomId && !skipSave) {
      saveSimpleFilters(roomId, filters);
    }
  }, [
    roomId,
    filters.myTasksOnly,
    filters.myRequestsOnly,
    filters.dueTodayOnly,
    filters.overdueOnly,
    filters.starredOnly,
    filters.showCompleted,
    filters.showHidden,
    skipSave,
  ]);

  // プロジェクト変更時にフィルターを読み込む関数を返す
  const loadFilters = useCallback(() => {
    return loadSimpleFilters(roomId);
  }, [roomId]);

  return { loadFilters };
}
